import Video from "$components/editor/extensions/blocks/Video/Video.svelte";
import { SvelteNodeViewRenderer } from "svelte-tiptap";
import { Alignment, Size } from "$components/editor/extensions/blocks/shared/attributes";
import { Node, mergeAttributes } from "@tiptap/core";
import { SvelteSSRRenderer } from "app/components/editor/utils";
import { type MarkdownSerializerState } from "prosemirror-markdown";
import { type Node as PMNode } from "@tiptap/pm/model";

export interface VideoOptions {
  inline: boolean;
  allowBase64: boolean;
  HTMLAttributes: Record<string, any>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    video: {
      /**
       * Add a video
       */
      setVideo: (options: { src: string; title?: string }) => ReturnType;
    };
  }
}

export const VideoBlock = Node.create<VideoOptions>({
  name: "video",

  addOptions() {
    return {
      inline: false,
      allowBase64: false,
      HTMLAttributes: {},
    };
  },

  inline() {
    return this.options.inline;
  },

  group() {
    return this.options.inline ? "inline" : "block";
  },

  draggable: true,
  selectable: true,
  addAttributes() {
    return {
      uuid: {
        default: null,
      },
      src: {
        default: null,
      },
      title: {
        default: null,
      },
      caption: {
        default: null,
      },
      width: { default: Size.MEDIUM },
      align: {
        default: Alignment.LEFT,
      },
      type: {
        default: "",
      },
      fileSize: {
        default: 0,
      },
      fileExtension: {
        default: "",
      },
    };
  },

  addNodeView() {
    return SvelteNodeViewRenderer(Video);
  },

  parseHTML() {
    return [
      {
        tag: this.options.allowBase64 ? "video[src]" : 'video[src]:not([src^="data:"])',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return SvelteSSRRenderer("video", Video, {
      node,
      attributes: mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
    });
  },

  addCommands() {
    return {
      setVideo:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },

  addStorage() {
    return {
      markdown: {
        serialize: (state: MarkdownSerializerState, node: PMNode) => {
          const src = node.attrs.src;
          const title = node.attrs.title || "Video";
          const caption = node.attrs.caption;

          // [Video title](https://example.com/video.mp4)
          state.write(`[${title}](${src})\n`);

          if (caption) {
            state.ensureNewLine();
            state.write(`*${caption}*`);
          }

          state.closeBlock(node);

          // Example output:
          // [Video title | 'Video'](https://example.com/video.mp4)
          // *Video caption*
        },
      },
    };
  },
});
